/**
 * M10: Shared Draft.js → sanitized HTML conversion helper.
 * Centralizes the convertToHTML config used across QuestionBank components
 * and applies DOMPurify (M9) to prevent XSS via dangerouslySetInnerHTML.
 */
import React from 'react';
import { convertFromRaw } from 'draft-js';
import { convertToHTML } from 'draft-convert';
import DOMPurify from 'dompurify';

/**
 * Convert a Draft.js ContentState to an HTML string with custom style/block/entity handling.
 * @param {import('draft-js').ContentState} contentState
 * @returns {string} sanitised HTML string
 */
export const draftContentStateToHtml = (contentState) => {
  const raw = convertToHTML({
    styleToHTML: (style) => {
      if (style === 'BOLD') return <strong />;
      if (style === 'ITALIC') return <em />;
      if (style === 'UNDERLINE') return <u />;
      if (style === 'STRIKETHROUGH') return <span style={{ textDecoration: 'line-through' }} />;
      if (style === 'SUPERSCRIPT') return <sup />;
      if (style === 'SUBSCRIPT') return <sub />;
      if (style.startsWith('color-')) {
        return <span style={{ color: style.replace('color-', '') }} />;
      }
      if (style.startsWith('bgcolor-')) {
        return <span />;
      }
      if (style.startsWith('fontsize-')) {
        const size = style.replace('fontsize-', '');
        return <span style={{ fontSize: `${size}px` }} />;
      }
    },
    blockToHTML: (block) => {
      if (block.type === 'header-one') return <h1 />;
      if (block.type === 'header-two') return <h2 />;
      if (block.type === 'header-three') return <h3 />;
      if (block.type === 'header-four') return <h4 />;
      if (block.type === 'header-five') return <h5 />;
      if (block.type === 'header-six') return <h6 />;
      if (block.type === 'blockquote') {
        return <blockquote style={{ borderLeft: '4px solid #ccc', paddingLeft: '16px', margin: '8px 0', fontStyle: 'italic' }} />;
      }
      if (block.type === 'unordered-list-item') return { element: <li />, nest: <ul /> };
      if (block.type === 'ordered-list-item') return { element: <li />, nest: <ol /> };
    },
    entityToHTML: (entity, originalText) => {
      if (entity.type === 'IMAGE') {
        const w = entity.data.width || '350';
        const widthStyle = typeof w === 'number' || /^\d+$/.test(w) ? `${w}px` : w;
        return `<img src="${DOMPurify.sanitize(entity.data.src)}" alt="${DOMPurify.sanitize(entity.data.alt || '')}" style="max-width: 100%; width: ${widthStyle}; height: auto;" />`;
      }
      if (entity.type === 'LINK') {
        return <a href={entity.data.url} target="_blank" rel="noopener noreferrer">{originalText}</a>;
      }
      return originalText;
    }
  })(contentState);

  // M9: Sanitize the final HTML to prevent XSS
  // Allow blob: URIs so ZIP-extracted image previews render in collapsed view
  return DOMPurify.sanitize(raw, {
    ADD_TAGS: ['img'],
    ADD_ATTR: ['target', 'rel', 'style'],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|ftp|mailto|tel|callto|sms|cid|xmpp|blob):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i
  });
};

/**
 * Convert a Draft.js raw JSON object to sanitised HTML.
 * Returns fallback text on parse failure.
 * @param {object} rawContent - Draft.js raw content (the object stored in Firestore)
 * @returns {string} sanitised HTML string, or empty string on failure
 */
export const draftRawToHtml = (rawContent) => {
  try {
    const contentState = convertFromRaw(rawContent);
    return draftContentStateToHtml(contentState);
  } catch (err) {
    console.error('draftRawToHtml: failed to convert Draft.js content', err);
    return '';
  }
};

/**
 * Render Draft.js raw content as a React element with sanitised HTML.
 * Drop-in replacement for the duplicated renderPassagePreview / renderQuestionPreview functions.
 * @param {object} rawContent - Draft.js raw content object
 * @returns {JSX.Element}
 */
export const renderDraftContent = (rawContent) => {
  const html = draftRawToHtml(rawContent);
  if (!html) {
    return <span className="text-muted">Unable to render content</span>;
  }
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
};

/**
 * Convert Draft.js raw content to plain text (for Excel export, etc.)
 * @param {object} rawContent - Draft.js raw content object
 * @returns {string} plain text string
 */
export const draftRawToPlainText = (rawContent) => {
  try {
    if (!rawContent) return '';
    const contentState = convertFromRaw(rawContent);
    return contentState.getPlainText('\n');
  } catch (err) {
    console.error('draftRawToPlainText: failed to convert Draft.js content', err);
    return '';
  }
};

/**
 * Convert plain text to a Draft.js raw content state object.
 * @param {string} text - Plain text string
 * @returns {object|null} Draft.js raw content object, or null if text is empty
 */
export const textToDraftRaw = (text) => {
  if (!text) return null;
  if (typeof text === 'object' && text.blocks) return text;
  return {
    blocks: [
      {
        key: Math.random().toString(36).substr(2, 5),
        text: text,
        type: 'unstyled',
        depth: 0,
        inlineStyleRanges: [],
        entityRanges: [],
        data: {}
      }
    ],
    entityMap: {}
  };
};

/**
 * Convert plain text to a Draft.js raw content state object with an optional image entity.
 * If imageUrl is null/undefined, delegates to textToDraftRaw().
 * If text is already a Draft.js raw object, deep clones it and appends the image block.
 * @param {string|object} text - Plain text string or Draft.js raw content object
 * @param {string|null} imageUrl - URL of the image to embed, or null to skip
 * @param {string|number} [width='350'] - Display width of the image in pixels
 * @returns {object|null} Draft.js raw content object with image entity appended
 */
export const textToDraftRawWithImage = (text, imageUrl, width) => {
  if (!imageUrl) return textToDraftRaw(text);

  // Get a base raw object to work with
  let raw;
  if (typeof text === 'object' && text.blocks) {
    // Deep clone the existing Draft.js raw object
    raw = JSON.parse(JSON.stringify(text));
  } else {
    raw = textToDraftRaw(text || ' ');
    if (!raw) raw = { blocks: [], entityMap: {} };
  }

  // Find the next entity key
  const existingKeys = Object.keys(raw.entityMap).map(Number);
  const nextEntityKey = existingKeys.length > 0 ? Math.max(...existingKeys) + 1 : 0;

  // Add the IMAGE entity
  raw.entityMap[nextEntityKey] = {
    type: 'IMAGE',
    mutability: 'IMMUTABLE',
    data: { src: imageUrl, width: width || '350', alt: '' }
  };

  // Append an atomic block that references this entity
  raw.blocks.push({
    key: Math.random().toString(36).substr(2, 5),
    text: ' ',
    type: 'atomic',
    depth: 0,
    inlineStyleRanges: [],
    entityRanges: [{ offset: 0, length: 1, key: nextEntityKey }],
    data: {}
  });

  // Add trailing empty block so the cursor can be placed after the image
  raw.blocks.push({
    key: Math.random().toString(36).substr(2, 5),
    text: '',
    type: 'unstyled',
    depth: 0,
    inlineStyleRanges: [],
    entityRanges: [],
    data: {}
  });

  return raw;
};

/**
 * Convert text with inline [IMG:filename] or [IMG:filename:width] placeholders
 * to a Draft.js raw content state object with multiple images at arbitrary positions.
 *
 * @param {string} text - Text with optional [IMG:filename] or [IMG:filename:width] placeholders
 * @param {object} imageUrlMap - Map of lowercase filename → image URL (blob or Firebase)
 * @param {number} [defaultWidth=350] - Default image width in pixels
 * @returns {object|null} Draft.js raw content object, or null if text is empty
 */
export const textToDraftRawWithInlineImages = (text, imageUrlMap = {}, defaultWidth = 350) => {
  if (!text) return null;
  if (typeof text === 'object' && text.blocks) return text;

  const IMG_REGEX = /\[IMG:([^\]]+)\]/g;

  if (!IMG_REGEX.test(text)) {
    return textToDraftRaw(text);
  }

  IMG_REGEX.lastIndex = 0;

  const blocks = [];
  const entityMap = {};
  let entityIndex = 0;
  let lastIndex = 0;
  let match;

  const makeKey = () => Math.random().toString(36).substr(2, 5);

  while ((match = IMG_REGEX.exec(text)) !== null) {
    const before = text.slice(lastIndex, match.index);
    if (before || blocks.length === 0) {
      blocks.push({
        key: makeKey(), text: before, type: 'unstyled', depth: 0,
        inlineStyleRanges: [], entityRanges: [], data: {}
      });
    }

    const parts = match[1].split(':');
    const filename = parts[0].trim();
    const width = parts[1] ? parseInt(parts[1].trim(), 10) || defaultWidth : defaultWidth;
    const imgUrl = imageUrlMap[filename.toLowerCase()] || '';

    entityMap[entityIndex] = {
      type: 'IMAGE', mutability: 'IMMUTABLE',
      data: { src: imgUrl, width: String(width), alt: filename }
    };

    blocks.push({
      key: makeKey(), text: ' ', type: 'atomic', depth: 0,
      inlineStyleRanges: [], entityRanges: [{ offset: 0, length: 1, key: entityIndex }], data: {}
    });

    entityIndex++;
    lastIndex = match.index + match[0].length;
  }

  const remaining = text.slice(lastIndex);
  blocks.push({
    key: makeKey(), text: remaining, type: 'unstyled', depth: 0,
    inlineStyleRanges: [], entityRanges: [], data: {}
  });

  return { blocks, entityMap };
};
